import { computed } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { getLocaleCodeForUrl } from '~~/common/utils/localeCode';
import { useConfigStore } from '~~/common/stores/config';
import { useTranslationStore } from '~~/common/stores/translation';

/**
 * The locale codes this instance is configured with, taken from setLocale so the
 * two cannot drift apart.
 */
type LocaleCode = Parameters<ReturnType<typeof useNuxtApp>['$i18n']['setLocale']>[0];

export function useLocaleCodeToUrl () {
  const { $i18n } = useNuxtApp();
  return computed(() => {
    return `/${getLocaleCodeForUrl({ $i18n })}`.replace(/\/$/, '');
  });
}

export function useGetLocaleCodeByUrl () {
  const { $i18n } = useNuxtApp();
  const locales = $i18n.locales;
  // The url is optional: the Salesforce callback has no `state` query parameter
  // to forward when the login did not start from a specific page.
  return function getLocaleCodeByUrl (url?: string) {
    const localeCodeFromUrl = (url ?? '')
      .split('/')
      .find(currentItem => currentItem.length)
    ;
    const match = locales.value.find(({ code }) => code === localeCodeFromUrl);
    return match ? match.code : $i18n.defaultLocale;
  };
}
export function useChangeLanguage () {
  const { $i18n, $logger } = useNuxtApp();
  const { fetchConfigCommon } = useConfigStore();
  const { fetchTranslations } = useTranslationStore();
  // `lowerCaseText` is the locale code in the form setLocale expects. It comes
  // from availableLocales in languageDataMixin.
  return async function changeLanguage (item: { lowerCaseText: LocaleCode }) {
    const localeCode = item.lowerCaseText;
    await fetchTranslations({ localeCode }).catch((e) => {
      $logger.error(e);
    });
    // setLocale navigates on its own; it resolves once the switch is done.
    await $i18n.setLocale(localeCode).finally(() => {
      setTimeout(() => {
        fetchConfigCommon();
      }, 100);
    });
  };
}
